/**
 * RecentOrders widget -- Last order from SelectO.
 */

import type { Widget, WidgetContext } from '../types';

interface RecentOrder {
  id: string;
  venue: string;
  status: 'preparing' | 'ready' | 'delivered' | 'cancelled';
  total: number;
  currency: string;
  timestamp: string;
}

function getMockOrder(): RecentOrder | null {
  try {
    const raw = localStorage.getItem('aweni_recent_order');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return null;
}

function getStatusIcon(status: string): string {
  switch (status) {
    case 'preparing': return '🍳';
    case 'ready': return '✅';
    case 'delivered': return '📦';
    case 'cancelled': return '❌';
    default: return '🍕';
  }
}

export const RecentOrders: Widget = {
  id: 'recent-orders',
  label: { en: 'Recent Order', fr: 'Derniere commande', ar: 'آخر طلب', es: 'Ultimo pedido', de: 'Letzte Bestellung' },
  icon: '🍕',
  defaultVisible: true,
  defaultOrder: 14,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const order = getMockOrder();

    if (!order) {
      container.innerHTML = '';
      return;
    }

    const statusLabel = ctx.t(`widget_order_${order.status}`);
    const icon = getStatusIcon(order.status);

    if (size === 'compact') {
      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer flex items-center justify-between" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="selecto">
          <div class="flex items-center gap-2">
            <span>${icon}</span>
            <span class="text-sm" style="color:var(--text-primary);">${ctx.t('widget_last_order')}: ${statusLabel}</span>
          </div>
          <span class="text-xs" style="color:var(--text-secondary);">${order.venue}</span>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="selecto">
          <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">🍕 ${ctx.t('widget_recent_order_title')}</h3>
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium" style="color:var(--text-primary);">${order.venue}</p>
              <p class="text-xs" style="color:var(--text-secondary);">${icon} ${statusLabel}</p>
            </div>
            <span class="text-sm font-bold" style="color:var(--text-primary);">
              ${new Intl.NumberFormat(undefined, { style: 'currency', currency: order.currency }).format(order.total)}
            </span>
          </div>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('selecto'));
    });
  },
};
